import React from "react";
import { withStyles, IconButton } from "@material-ui/core";
import { compose } from "recompose";
import clsx from "clsx";
import Tooltip from "../../../common/StyledToolTip";
import { SavePreferences } from "../../../../utilities/appHelper";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { KPIRow, Retry, AutoRefreshKPI } from "./components";
import appConstants from "../../../../appConstants";
import styles from "./styles";
import { AppLoading } from "../../../../loaders/loaders";
import Events from "../../../../utilities/EventEmitter";
import ReturnShipments from "./ReturnShipments";
import { getDataWithDeduplication } from "../../../../api/componentAPIs";
import PriorityShipments from "./PriorityShipments";
import { observer, inject } from "mobx-react";
import { withAbortController } from "../../../common/HOC/withAbortController";

@inject("DispatchStore")
@observer
class KPIPanel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expand: false,
      isDataLoaded: false,
      kpiData: [],
    };
    this.expandHeight = 128;
    this.initialHeight = this.props.glContainer.height + 10;
  }

  componentDidMount() {
    this.loadDispatchKPIData(true);
    Events.on("refreshDispatchKPI", this.loadDispatchKPIData);
    Events.on("updateKPIData", this.updateKPIData);
  }

  componentWillUnmount() {
    Events.off("refreshDispatchKPI", this.loadDispatchKPIData);
    Events.off("updateKPIData", this.updateKPIData)
  }

  setSavedExpandPreferences = () => {
    if (!this.state.isDataLoaded || this.state.kpiData.length === 0) {
      return;
    }
    const expand = this.getSavedExpandPreferences();    
    if (this.state.expand !== expand) {
      this.setState(
        {
          expand
        },
        this.setContainerHeight
      );
    }
  };

  setLoadingStatus = () => {
    this.setState({
      isDataLoaded: false,
    });
  };

  updateKPIData=(kpiData)=>{
    if (!this.props.isMountedRef.current) return;
    
    this.setState({
      kpiData: kpiData
    });
    Events.emit("SetDispatchKPIRefreshTime");
  }

  loadDispatchKPIData = (showLoadingEffect = false) => {
    showLoadingEffect && this.setLoadingStatus();
    const apiName = "DispatchKPI";
    const signal = this.props.getNewAbortSignal(apiName);
    getDataWithDeduplication(
      apiName,
      `terminalCode=${this.props.userData.userProfile.terminal.code}`,
      null,
      signal
    )
      .then((response) => {
        if (!this.props.isMountedRef.current) return;
        
        if (response?.status === 200) {
          this.setState({
            kpiData: response.data,
            isDataLoaded: true,
          });
          Events.emit("SetDispatchKPIRefreshTime");
        } else {
          this.handleError();
        }
        this.setSavedExpandPreferences();
      })
      .catch((_error) => {
        if (!this.props.isMountedRef.current) return;
        this.handleError();
      });
  };

  handleError = () => {
    this.setState({
      kpiData: [],
      isDataLoaded: true,
    });
  };

  getSavedExpandPreferences = () => {
    const isKPIExpanded =
      this.props.userData.userProfile?.columnPref?.[
        `${appConstants.KPI_NAME.DispatchKPI}_Config`
      ]?.isKPIExpanded ?? false;
    return isKPIExpanded;
  };

  setContainerHeight = () => {
    const newHeight =
      this.state.isDataLoaded && this.state.expand
        ? this.expandHeight
        : this.initialHeight;
    this.props.glContainer.parent.container.setSize(false, newHeight);
  };

  handleExpandClick = () => {
    this.setState(
      (prevState) => ({
        expand: !prevState.expand,
      }),
      () => {
        this.saveKPIExpandPreference();
        this.setContainerHeight();
      }
    );
  };

  saveKPIExpandPreference = () => {
    const { expand } = this.state;
    let { columnPref, userId } = this.props.userData.userProfile;
    if (!columnPref) {
      columnPref = {};
    }
    const storedConfig =
      columnPref[`${appConstants.KPI_NAME.DispatchKPI}_Config`] ?? {};
    SavePreferences({
      name: `${appConstants.KPI_NAME.DispatchKPI}_Config`,
      config: { ...storedConfig, isKPIExpanded: expand },
      userColumnPref: columnPref,
      userId,
    });
  };

  render() {
    const kpiData = this.state.kpiData;
    const pivotPoint = 3;
    const firstRowKPIData = kpiData.slice(0, pivotPoint);
    const secondRowKPIData = kpiData.slice(pivotPoint);
    const { classes } = this.props;
    const { expand, isDataLoaded } = this.state;
    if (!isDataLoaded) {
      return (
        <div id="appContainer" className={classes.layout}>
          <AppLoading />
        </div>
      );
    } else if (kpiData.length === 0) {
      return <Retry onClick={() => this.loadDispatchKPIData(true)} />;
    }
    return (
      <div className={`${classes.kpiContainer} customScroll`}>
        <div className={classes.rowContainer}>
          <Tooltip
            title={expand ? "Expand less" : "Expand more"}
            disableFocusListener
          >
            <IconButton
              className={clsx(classes.expand, {
                [classes.expandOpen]: expand,
              })}
              onClick={this.handleExpandClick}
              aria-expanded={expand}
            >
              <ExpandMoreIcon className={classes.expandIcon} />
            </IconButton>
          </Tooltip>
          <AutoRefreshKPI
            onRefreshClick={this.loadDispatchKPIData}
            terminalOffset={this.props.userData.userProfile.terminal.offset}
          />
          <KPIRow rowData={firstRowKPIData} priorityShipmentList={this.props.DispatchStore.priorityShipmentList}/>
        </div>
        {expand && (
          <div className={classes.lastRowContainer}>
            <KPIRow rowData={secondRowKPIData} priorityShipmentList={this.props.DispatchStore.priorityShipmentList}/>
          </div>
        )}
        <ReturnShipments userProfile={this.props.userData.userProfile} />
        <PriorityShipments userProfile={this.props.userData.userProfile} glEventHub={this.props.glEventHub}/>
      </div>
    );
  }
}

export default compose(withStyles(styles), withUserProfile, withAbortController)(KPIPanel);
